import { ThunkDispatch } from "core-integration/src/store";
import { updateEntry } from "core-integration/src/store/entries/thunks";
import {
  IEntry,
  IUpdateEntryData,
} from "core-integration/src/store/entries/types";
import { getEvents } from "core-integration/src/store/events/reducers";
import { getTeams } from "core-integration/src/store/teams/reducers";
import { formatRawAsLocal } from "core-integration/src/utils/datetime";
import { Alert } from "plos/src/components/alerts";
import { Button } from "plos/src/components/buttons/Button";
import { FormSelect } from "plos/src/components/forms";
import { FormTextField } from "plos/src/components/forms/FormTextField";
import HorizontalDivider from "plos/src/components/HorizontalDivider";
import Subheading from "plos/src/components/Subheading";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { ChangeEvent, SyntheticEvent, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  containerStyles,
  explainText,
  favouriteClubTitle,
  headerStyles,
  inputWrapper,
} from "./updateTeamDetails.css";

type FormState = {
  favourite_team: IEntry["favourite_team"];
  name: IEntry["name"];
};

interface UpdateTeamDetailsProps {
  entry: IEntry;
}

const checkForChanges = ({
  formState,
  entry,
}: {
  formState: FormState;
  entry: IEntry;
}) => {
  const { favourite_team: faveTeam, name: nameValue } = formState;

  const faveValue = faveTeam === -1 ? null : faveTeam;

  return nameValue === entry.name && faveValue === entry.favourite_team;
};

const UpdateTeamDetails = ({ entry }: UpdateTeamDetailsProps) => {
  const { t } = useTranslation("common");
  const [hasUpdate, setHasUpdate] = useState(false);
  const [formState, setFormState] = useState<FormState>({
    favourite_team: entry.favourite_team,
    name: entry.name,
  });

  const dispatch = useDispatch<ThunkDispatch>();
  const teams = useSelector(getTeams);
  const events = useSelector(getEvents);

  const handleNameChange = (value: string) => {
    setFormState({ ...formState, name: value });
  };

  const handleFaveChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setFormState({
      ...formState,
      favourite_team: parseInt(e.currentTarget.value, 10),
    });
  };

  const handleUpdateEntry = async (data: IUpdateEntryData) => {
    try {
      await dispatch(updateEntry(data));
      setHasUpdate(true);
      setTimeout(() => setHasUpdate(false), 3000);
    } catch (error) {
      console.error("Failed to update entry", error);
    }
  };

  const handleUpdateTeamDetails = () => {
    const newName = formState.name;
    const newFavouriteTeam = formState.favourite_team;

    handleUpdateEntry({
      name: newName,
      favourite_team: newFavouriteTeam === -1 ? null : newFavouriteTeam,
      kit: null,
      email: false,
    });
  };

  const formHasNoChanges = checkForChanges({ formState, entry });
  const emptyNameValue = !formState.name;

  const updateIsDisabled = formHasNoChanges || emptyNameValue;

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    handleUpdateTeamDetails();
  };

  return (
    <SurfaceContainer>
      <form className={containerStyles} onSubmit={handleSubmit}>
        <div className={headerStyles}>
          <Subheading>
            {t("updateTeamDetails.myDetails", "My Details")}
          </Subheading>
          {hasUpdate && (
            <Alert isContentCentered variant={"success"}>
              {t("updateTeamDetails.updated", "  Updated")}
            </Alert>
          )}
        </div>

        <span className={inputWrapper}>
          <FormTextField
            label={t("updateTeamDetails.teamName", "Team Name")}
            value={formState.name}
            placeholder={t("enterNameHere", "Enter Name Here") as string}
            onChange={(val) => handleNameChange(val)}
            maxLength={20}
            isDisabled={entry.name_change_blocked}
          />
          <span className={explainText}>
            <Trans ns="common" i18nKey={"updateTeamDetails.nameExplanation"}>
              Please think carefully before entering your team name. Teams with
              names that are deemed inappropriate or offensive may result in
              your account being deleted. Please refer to the Terms & Conditions
              of entry for more information.
            </Trans>
          </span>
        </span>
        <HorizontalDivider />
        <span className={inputWrapper}>
          <h4 className={favouriteClubTitle}>
            {t("updateTeamDetails.favouriteClub", "Favourite Club")}
          </h4>
          <span className={explainText}>
            <Trans
              ns="common"
              i18nKey={"updateTeamDetails.favouriteClubExplanation"}
              values={{ deadline: formatRawAsLocal(events[0].deadline_time) }}
            >
              Choose your favourite Premier League club if you would like to be
              entered into a supporters league. If you do not select an option
              before {"{{ deadline }}"}, you will not be entered into a
              supporters league.
            </Trans>
          </span>
        </span>
        <FormSelect
          id="favourite-club"
          label={
            t("updateTeamDetails.favouriteClub", "Favourite Club") as string
          }
          value={
            formState.favourite_team === null ? -1 : formState.favourite_team
          }
          onChange={handleFaveChange}
        >
          {teams.map(({ id, name }) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
          <option key={-1} value={-1}>
            {t("updateTeamDetails.none", "None")}
          </option>
        </FormSelect>
        <Button
          type="submit"
          fullWidth
          size="small"
          isDisabled={updateIsDisabled}
        >
          {t("updateTeamDetails.updateDetails", "Update Details")}
        </Button>
      </form>
    </SurfaceContainer>
  );
};

export default UpdateTeamDetails;
